import assert from 'minimalistic-assert';
import { deserializeTransaction } from '@exodus/web3-solana-utils';
import { validateFillMessage } from './validateFillMessage.js';
import { NEW_TOKEN_ACCOUNT_FEE, SOL_ADDRESS, UNKNOWN_FEES_BUFFER, whitelistedSimulationErrors, WRAPPED_SOL_ADDRESS, } from '../providers/indirect/constants.js';
import BaseTransaction from './BaseTransaction.js';
import { InvalidAmountError, InvalidFromTokenAddressError, InvalidToTokenAddressError, JupiterSimulationError, } from '../errors.js';
const CHAINED_ORDERS_AMOUNT_BUFFER = 0.05;
function inferSolFeeUnitTypeFromWillPayFee(willPayFee) {
    for (const { asset, balance } of willPayFee) {
        if (!asset)
            continue;
        const addr = asset.address;
        if (addr === SOL_ADDRESS || addr === WRAPPED_SOL_ADDRESS)
            return balance.unitType;
        if (asset.symbol === 'SOL' && !addr)
            return balance.unitType;
    }
    return undefined;
}
function isSolNetworkBroadcastFeeRow(row, solFeeUnitTypeInferred) {
    const { asset, balance } = row;
    if (!asset) {
        if (solFeeUnitTypeInferred !== undefined) {
            return solFeeUnitTypeInferred.equals(balance.unitType);
        }
        return true;
    }
    const { address: addr, symbol } = asset;
    if (addr === SOL_ADDRESS || addr === WRAPPED_SOL_ADDRESS)
        return true;
    return symbol === 'SOL' && !addr;
}
function sumSolNetworkBroadcastFee(willPayFee, solFeeUnitTypeInferred) {
    const solFeeRows = willPayFee.filter((row) => isSolNetworkBroadcastFeeRow(row, solFeeUnitTypeInferred));
    if (solFeeRows.length === 0)
        return undefined;
    const zero = solFeeRows[0].balance.sub(solFeeRows[0].balance);
    return solFeeRows.reduce((acc, { balance }) => acc.add(balance), zero);
}
class SolanaTransaction extends BaseTransaction {
    data;
    #networkBroadcastFeeSol;
    constructor(params) {
        super({ action: params.action, type: 'solana', kind: params.kind ?? 'amm' });
        this.data = params.data;
    }
    async validate(params) {
        if (this.kind === 'rfq') {
            assert(params.fromAsset.solana?.mintAddress, `Missing solana mintAddress for ${params.fromAsset.id}`);
            assert(params.toAsset.solana?.mintAddress, `Missing solana mintAddress for ${params.toAsset.id}`);
            validateFillMessage(this.data, {
                taker: params.fromAddress,
                inputMint: params.fromAsset.solana.mintAddress,
                outputMint: params.toAsset.solana.mintAddress,
                inAmount: BigInt(params.fromAmount.toBaseString()),
                outAmount: BigInt(params.toAmount.toBaseString()),
            });
            return;
        }
        const previewSolanaTxs = (params.requiredPreviewTxs ?? []).filter((tx) => tx.type === 'solana');
        const deserializedTranasction = deserializeTransaction(this.data);
        const simulationResults = await params.simulateTx({
            baseAssetName: 'solana',
            txs: [
                ...previewSolanaTxs.map((tx) => deserializeTransaction(tx.data).serialize({ verifySignatures: false })),
                deserializedTranasction.serialize({ verifySignatures: false }),
            ],
            senderAddress: params.fromAddress,
            origin: 'jupiter',
            ...(previewSolanaTxs.length > 0 && { executionMode: 'jito_bundle' }),
        });
        const { balanceChanges: { willSend = [], willReceive = [], willPayFee = [] } = {}, metadata: { humanReadableError = undefined } = {}, } = simulationResults || {};
        const solFeeUnitTypeInferred = inferSolFeeUnitTypeFromWillPayFee(willPayFee);
        this.#networkBroadcastFeeSol = sumSolNetworkBroadcastFee(willPayFee, solFeeUnitTypeInferred);
        if (humanReadableError && whitelistedSimulationErrors.has(humanReadableError)) {
            return;
        }
        if (humanReadableError || willSend.length === 0) {
            throw new JupiterSimulationError({
                message: humanReadableError || 'Simulation failed',
            });
        }
        const isFromSOL = params.fromAsset.id === 'SOL';
        const feesToPay = isFromSOL
            ? willPayFee
                .filter((row) => isSolNetworkBroadcastFeeRow(row, solFeeUnitTypeInferred))
                .reduce((acc, { balance }) => acc.add(balance), params.fromAsset.currency.ZERO)
            : null;
        const sendingBalance = isFromSOL
            ? willSend
                .reduce((acc, { balance }) => acc.add(balance), params.fromAsset.currency.defaultUnit(0))
                .add(feesToPay)
            : willSend
                .filter(({ asset }) => asset?.address === params.fromAsset.solana?.mintAddress)
                .reduce((acc, { balance }) => {
                const balanceInAssetUnit = params.fromAsset.currency.baseUnit(balance.toBaseString());
                return acc.add(balanceInAssetUnit);
            }, params.fromAsset.currency.defaultUnit(0));
        const baseAmount = params.swapMode === 'ExactIn'
            ? params.isChained
                ? params.fromAmount.mul(1 + CHAINED_ORDERS_AMOUNT_BUFFER)
                : params.fromAmount
            : params.fromAmount.mul(1 + params.slippage / 100);
        const maxAllowedToSpend = isFromSOL
            ? baseAmount.add(params.fromAsset.currency.defaultUnit(NEW_TOKEN_ACCOUNT_FEE * 2 + UNKNOWN_FEES_BUFFER))
            : baseAmount;
        const isValidSendingBalance = maxAllowedToSpend.gte(sendingBalance);
        if (!isValidSendingBalance) {
            throw new InvalidAmountError({
                order: null,
                providerOrder: this.toJSON(),
                actual: sendingBalance.toDefaultString({ unit: false }),
                expected: baseAmount.toDefaultString({ unit: false }),
            });
        }
        const isValidFromToken = willSend.every((send) => [
            params.fromAsset.solana?.mintAddress || SOL_ADDRESS,
            SOL_ADDRESS,
            WRAPPED_SOL_ADDRESS,
        ].includes(send.asset?.address || ''));
        if (!isValidFromToken) {
            throw new InvalidFromTokenAddressError({
                order: null,
                providerOrder: this.toJSON(),
                actual: willSend.map((send) => send.asset?.address)[0] ?? '',
                expected: params.fromAsset.solana?.mintAddress || SOL_ADDRESS,
            });
        }
        const isCrossChainSwap = params.fromAsset.network !== params.toAsset.network;
        const isValidToToken = isCrossChainSwap ||
            willReceive.every((receive) => [
                params.toAsset.solana?.mintAddress || SOL_ADDRESS,
                SOL_ADDRESS,
                WRAPPED_SOL_ADDRESS,
            ].includes(receive.asset?.address || ''));
        if (!isValidToToken) {
            throw new InvalidToTokenAddressError({
                order: null,
                providerOrder: this.toJSON(),
                actual: willReceive.map((receive) => receive.asset?.address)[0] ?? '',
                expected: params.toAsset.solana?.mintAddress || SOL_ADDRESS,
            });
        }
        if (!isCrossChainSwap) {
            const toMint = params.toAsset.solana?.mintAddress || SOL_ADDRESS;
            const receivingBalance = willReceive
                .filter(({ asset }) => (asset?.address || '') === toMint)
                .reduce((acc, { balance }) => {
                const balanceInAssetUnit = params.toAsset.currency.baseUnit(balance.toBaseString());
                return acc.add(balanceInAssetUnit);
            }, params.toAsset.currency.defaultUnit(0));
            const minExpectedReceive = params.toAmount.mul(1 - params.slippage / 100);
            if (receivingBalance.lt(minExpectedReceive)) {
                throw new InvalidAmountError({
                    order: null,
                    providerOrder: this.toJSON(),
                    actual: receivingBalance.toDefaultString({ unit: false }),
                    expected: params.toAmount.toDefaultString({ unit: false }),
                });
            }
        }
    }
    async execute(params) {
        const { fromAddress, toAddress } = params;
        assert(fromAddress, 'SolanaTransaction.execute requires fromAddress (maps to signAndSendTx sender / txLog metadata)');
        assert(toAddress, 'SolanaTransaction.execute requires toAddress (maps to signAndSendTx recipient / txLog metadata)');
        const { txId } = await params.signAndSendTx({
            assetId: params.fromAsset.id,
            rawTx: this.data,
            amount: params.amount,
            fee: this.#networkBroadcastFeeSol,
            sender: fromAddress,
            recipient: toAddress,
        });
        return txId;
    }
    toJSON() {
        return {
            data: this.data,
            action: this.action,
            type: this.type,
            kind: this.kind,
        };
    }
}
export default SolanaTransaction;
